import React, { useEffect, useState } from "react";
import { Card, Col, Row, Badge, Button, Modal, Table, Spinner } from "react-bootstrap";
import { Link } from "react-router-dom";
import axiosInstance from "../services/axiosInterceptor";
import { getUserId } from "../services/authorize";
import { useLoading } from "../contexts/LoadingContext";
import { useLanguage } from "../contexts/LanguageContext";
import th   from "../locales/islamic/th";
import jawi from "../locales/islamic/jawi";

export default function IslamicSubjectList() {
  const { setLoading } = useLoading();
  const { lang } = useLanguage();
  const t = lang === 'th' ? th : jawi;

  const [subjects, setSubjects] = useState([]);
  const [yearId, setYearId]     = useState(() => localStorage.getItem("yearId") || null);
  const [yearList, setYearList] = useState([]);
  const [selectedYear, setSelectedYear] = useState("");

  useEffect(() => {
    setLoading(true);
    axiosInstance.get(`${process.env.REACT_APP_API}/getStudyYear`)
      .then(res => {
        const list = res.data;
        setYearList(list);
        let id = localStorage.getItem("yearId");
        if (!id && list.length > 0) {
          id = list[0].year_id;
          localStorage.setItem("yearId", id);
        }
        setYearId(id);
        const found = list.find(y => String(y.year_id) === String(id));
        setSelectedYear(found ? t.common.studyYear(found.year_year) : "");
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [setLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!yearId) return;
    const t_id = getUserId();
    setLoading(true);
    axiosInstance.get(`${process.env.REACT_APP_API}/islamic-teaching/mySubjects/${t_id}/${yearId}`)
      .then(res => setSubjects(res.data))
      .catch(() => setSubjects([]))
      .finally(() => setLoading(false));
  }, [yearId, setLoading]);

  const handleYearChange = (e) => {
    const id = e.target.value;
    localStorage.setItem("yearId", id);
    setYearId(id);
    const found = yearList.find(y => String(y.year_id) === String(id));
    setSelectedYear(found ? t.common.studyYear(found.year_year) : "");
  };

  const grouped = subjects.reduce((acc, s) => {
    const key = s.ir_id;
    if (!acc[key]) acc[key] = { room: s, subjects: [] };
    acc[key].subjects.push(s);
    return acc;
  }, {});

  const today = new Date().getDay();

  const parseSlots = (slot_summary) => {
    if (!slot_summary) return [];
    return slot_summary.split(',').map(pair => {
      const [day, order, begin, end, periodId] = pair.split('|');
      return { day: Number(day), order: Number(order), begin, end, periodId: Number(periodId) };
    });
  };

  const isTodayScheduled = (slot_summary) =>
    parseSlots(slot_summary).some(s => s.day === today);

  const renderSchedule = (slot_summary) => {
    const slots = parseSlots(slot_summary);
    if (slots.length === 0) return <span className="text-muted small">—</span>;
    const groups = slots.reduce((acc, s) => {
      if (!acc[s.day]) acc[s.day] = [];
      acc[s.day].push(s);
      return acc;
    }, {});
    return (
      <div className="d-flex flex-wrap gap-1">
        {Object.entries(groups).map(([day, items]) => (
          <div key={day} className="d-flex flex-column gap-0">
            <span className="badge bg-light text-dark border small">
              {t.days[Number(day)]}
              {items.map(s => (
                <span key={s.order} className="ms-1">
                  {s.order} <span className="text-muted">({s.begin}–{s.end})</span>
                </span>
              ))}
            </span>
          </div>
        ))}
      </div>
    );
  };

  const genderLabel = (g) => g === 'male' || g === 'm' ? t.common.male : t.common.female;

  const [showStudents, setShowStudents]           = useState(false);
  const [studentsRoom, setStudentsRoom]           = useState(null);
  const [studentsList, setStudentsList]           = useState([]);
  const [loadingStudents, setLoadingStudents]     = useState(false);
  const [selectedStudent, setSelectedStudent]     = useState(null);
  const [studentDetail, setStudentDetail]         = useState(null);
  const [loadingDetail, setLoadingDetail]         = useState(false);

  const handleShowStudents = (room) => {
    setStudentsRoom(room);
    setStudentsList([]);
    setShowStudents(true);
    setLoadingStudents(true);
    axiosInstance.get(`${process.env.REACT_APP_API}/islamic/getStudentsInRoom/${room.ir_id}`)
      .then(res => setStudentsList(res.data))
      .catch(() => setStudentsList([]))
      .finally(() => setLoadingStudents(false));
  };

  return (
    <React.Fragment>
      <div className="d-flex align-items-center justify-content-between mb-4">
        <div>
          <h4 className="main-title mb-0">{t.subjectList.pageTitle}</h4>
          <p className="text-secondary mb-0">{selectedYear}</p>
        </div>
        <select className="form-select w-auto" value={yearId || ""} onChange={handleYearChange}>
          {yearList.map(y => (
            <option key={y.year_id} value={y.year_id}>{t.common.studyYear(y.year_year)}</option>
          ))}
        </select>
      </div>

      {Object.keys(grouped).length === 0 ? (
        <Card className="card-one">
          <Card.Body className="text-center text-secondary py-5">{t.subjectList.noTeaching}</Card.Body>
        </Card>
      ) : (
        Object.values(grouped).map(({ room, subjects: subs }) => (
          <Card className="card-one mb-3" key={room.ir_id}>
            <Card.Header className="d-flex align-items-center">
              <span className="fw-semibold me-2">
                {t.common.classNo(room.ic_class, room.ir_no)}
              </span>
              <Badge bg="info" className="me-2">{room.is_programCode}</Badge>
              <Badge bg={room.ir_gender === 'male' ? 'primary' : 'danger'}>
                {genderLabel(room.ir_gender)}
              </Badge>
              <span className="ms-auto text-secondary small me-2">
                {t.common.studentCount(room.student_count)}
              </span>
              <Button size="sm" variant="outline-info" onClick={() => handleShowStudents(room)}>
                <i className="ri-group-line me-1"></i>{t.subjectList.studentListBtn}
              </Button>
            </Card.Header>
            <Card.Body className="p-0">
              <div className="table-responsive">
              <table className="table table-hover mb-0 text-nowrap">
                <thead className="table-secondary">
                  <tr>
                    <th>{t.subjectList.colCode}</th>
                    <th>{t.subjectList.colName}</th>
                    <th>{t.subjectList.colSchedule}</th>
                    <th className="text-center">{t.subjectList.colAttend}</th>
                    <th className="text-center">{t.attendanceStat.colStat}</th>
                    <th className="text-center">{t.subjectList.colScore}</th>
                  </tr>
                </thead>
                <tbody>
                  {subs.map(s => (
                    <tr key={s.is_teachId}>
                      <td className="align-middle">{s.isubject_isid1}</td>
                      <td className="align-middle arabic-text">{s.isubject_name}</td>
                      <td className="align-middle">{renderSchedule(s.slot_summary)}</td>
                      <td className="text-center align-middle">
                        {isTodayScheduled(s.slot_summary) ? (
                          <Link
                            to={`/islamic/check-attendance/${s.ir_id}/${s.is_teachId}`}
                            state={{ periods: parseSlots(s.slot_summary).filter(sl => sl.day === today).map(sl => sl.periodId).filter(Boolean) }}
                            className="btn btn-sm btn-outline-primary"
                          >
                            <i className="ri-checkbox-circle-line me-1"></i>{t.subjectList.attendBtn}
                          </Link>
                        ) : (
                          <span className="text-muted small">—</span>
                        )}
                      </td>
                      <td className="text-center align-middle">
                        <Link
                          to={`/islamic/attendance-statistic/${s.is_teachId}`}
                          className="btn btn-sm btn-outline-secondary"
                        >
                          <i className="ri-bar-chart-2-line me-1"></i>{t.attendanceStat.statBtn}
                        </Link>
                      </td>
                      <td className="text-center align-middle">
                        <Link
                          to={`/islamic/score-entry/${s.is_teachId}`}
                          className="btn btn-sm btn-outline-success"
                        >
                          <i className="ri-quill-pen-line me-1"></i>{t.subjectList.scoreBtn}
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            </Card.Body>
          </Card>
        ))
      )}

      {/* Student list modal */}
      <Modal show={showStudents} onHide={() => setShowStudents(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {t.subjectList.studentListBtn} —{" "}
            {studentsRoom && (
              <span className="text-secondary fs-6">
                {t.common.classNo(studentsRoom.ic_class, studentsRoom.ir_no)}
                <Badge bg={studentsRoom.ir_gender === 'male' ? 'primary' : 'danger'} className="ms-2">
                  {genderLabel(studentsRoom.ir_gender)}
                </Badge>
              </span>
            )}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-0">
          {loadingStudents ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
            </div>
          ) : studentsList.length === 0 ? (
            <p className="text-center text-secondary py-4">{t.subjectList.noStudents}</p>
          ) : (
            <Table hover responsive className="mb-0">
              <thead className="table-secondary">
                <tr>
                  <th style={{ width: 50 }} className="text-center">#</th>
                  <th>{t.subjectList.studentId}</th>
                  <th>{t.subjectList.colCode}</th>
                  <th>{t.scoreEntry.colName}</th>
                </tr>
              </thead>
              <tbody>
                {studentsList.map((s, idx) => (
                  <tr
                    key={s.is_study_id}
                    className={s.st_status === '0' ? 'table-danger' : ''}
                    style={{ cursor: 'pointer' }}
                    onClick={() => {
                      setSelectedStudent(s);
                      setStudentDetail(null);
                      setLoadingDetail(true);
                      axiosInstance.get(`${process.env.REACT_APP_API}/islamic/studentDetail/${s.st_id}`)
                        .then(res => setStudentDetail(res.data))
                        .catch(() => setStudentDetail(null))
                        .finally(() => setLoadingDetail(false));
                    }}
                  >
                    <td className="text-center text-secondary align-middle">{idx + 1}</td>
                    <td className="align-middle">{s.st_idnumber}</td>
                    <td className="align-middle arabic-text">{s.st_arabicname || <span className="text-muted">—</span>}</td>
                    <td className="align-middle">{s.st_name}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Modal.Body>
        <Modal.Footer>
          <span className="text-secondary small me-auto">{t.common.personCount(studentsList.length)}</span>
          <Button variant="secondary" onClick={() => setShowStudents(false)}>{t.common.close}</Button>
        </Modal.Footer>
      </Modal>

      {/* Student detail modal */}
      <Modal show={!!selectedStudent} onHide={() => setSelectedStudent(null)} centered size="lg" scrollable>
        <Modal.Header closeButton>
          <Modal.Title>{t.subjectList.studentInfo}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedStudent && (
            <>
              <div className="text-center mb-3">
                <div
                  className="rounded-circle bg-secondary d-inline-flex align-items-center justify-content-center text-white fw-bold mb-2"
                  style={{ width: 72, height: 72, fontSize: 28 }}
                >
                  {selectedStudent.st_name?.charAt(0) || '?'}
                </div>
                <h5 className="mb-0">{selectedStudent.st_arabicname}</h5>
                {selectedStudent.st_name && (
                  <p className="text-secondary arabic-text mb-0">{selectedStudent.st_name}</p>
                )}
              </div>

              {loadingDetail ? (
                <div className="text-center py-3"><Spinner animation="border" size="sm" /></div>
              ) : (() => {
                const d = studentDetail || selectedStudent;
                const calcAge = (birthdate) => {
                  if (!birthdate) return '—';
                  const b = new Date(birthdate);
                  if (isNaN(b)) return '—';
                  const age = new Date().getFullYear() - b.getFullYear();
                  const m = new Date().getMonth() - b.getMonth();
                  return (m < 0 || (m === 0 && new Date().getDate() < b.getDate())) ? age - 1 : age;
                };
                const fmtDate = (v) => v ? new Date(v).toLocaleDateString('en-GB') : '—';
                const val = (v) => v || <span className="text-muted">—</span>;
                const currentAddr = [d.st_current, d.st_currentSubDistrict, d.st_currentDistrict, d.st_currentProvince].filter(Boolean).join(', ');
                const oldAddr = [d.st_oldaddress, d.st_oldSubDistrict, d.st_oldDistrict, d.st_oldProvince].filter(Boolean).join(', ');
                const address = currentAddr || oldAddr || null;
                return (
                  <table className="table table-sm table-bordered mb-0">
                    <tbody>
                      <tr className="table-secondary"><th colSpan={2} className="small text-uppercase">{t.subjectList.identity}</th></tr>
                      <tr><th style={{ width: '38%' }}>{t.subjectList.studentId}</th><td>{val(d.st_idnumber)}</td></tr>
                      <tr><th>{t.subjectList.idCard}</th><td>{val(d.st_idcard)}</td></tr>
                      <tr><th>{t.subjectList.gender}</th>
                        <td><Badge bg={d.st_gen === 'm' ? 'info' : 'danger'}>{d.st_gen === 'm' ? t.common.male : t.common.female}</Badge></td>
                      </tr>
                      <tr><th>{t.subjectList.status}</th>
                        <td><Badge bg={d.st_status === '0' ? 'danger' : 'success'}>{d.status_text || (d.st_status === '0' ? t.common.withdrawn : t.common.active)}</Badge></td>
                      </tr>
                      <tr><th>{t.subjectList.birthDate}</th><td>{fmtDate(d.st_birthdate)}</td></tr>
                      <tr><th>{t.subjectList.age}</th><td>{calcAge(d.st_birthdate)} {t.common.years}</td></tr>

                      <tr className="table-secondary"><th colSpan={2} className="small text-uppercase">{t.subjectList.address}</th></tr>
                      <tr><th>{t.subjectList.address}</th><td>{address ? val(address) : <span className="text-muted">—</span>}</td></tr>

                      <tr className="table-secondary"><th colSpan={2} className="small text-uppercase">{t.subjectList.father}</th></tr>
                      <tr><th>{t.subjectList.fatherName}</th><td>{val(d.st_fatherName)}</td></tr>
                      <tr><th>{t.subjectList.fatherPhone}</th><td>{val(d.st_fatherPhone)}</td></tr>

                      <tr className="table-secondary"><th colSpan={2} className="small text-uppercase">{t.subjectList.mother}</th></tr>
                      <tr><th>{t.subjectList.motherName}</th><td>{val(d.st_motherName)}</td></tr>
                      <tr><th>{t.subjectList.motherPhone}</th><td>{val(d.st_motherPhone)}</td></tr>

                      {(d.st_parentname || d.st_parentPhone) && (<>
                        <tr className="table-secondary"><th colSpan={2} className="small text-uppercase">{t.subjectList.guardian}</th></tr>
                        <tr><th>{t.subjectList.guardianName}</th><td>{val(d.st_parentname)}</td></tr>
                        {d.st_parentRelation && <tr><th>{t.subjectList.guardianRelation}</th><td>{val(d.st_parentRelation)}</td></tr>}
                        <tr><th>{t.subjectList.guardianPhone}</th><td>{val(d.st_parentPhone)}</td></tr>
                      </>)}

                      <tr className="table-secondary"><th colSpan={2} className="small text-uppercase">{t.subjectList.dormitory}</th></tr>
                      {d.dorm_name ? (<>
                        <tr><th>{t.subjectList.dormName}</th><td>{d.dorm_name}{d.dorm_code && <span className="ms-1 text-muted small">({d.dorm_code})</span>}</td></tr>
                        {d.village_name && <tr><th>{t.subjectList.village}</th><td>{d.village_name}</td></tr>}
                        <tr><th>{t.subjectList.checkinDate}</th><td>{fmtDate(d.checkin_date)}</td></tr>
                      </>) : (
                        <tr><td colSpan={2} className="text-muted">{t.subjectList.noDorm}</td></tr>
                      )}
                    </tbody>
                  </table>
                );
              })()}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setSelectedStudent(null)}>{t.common.close}</Button>
        </Modal.Footer>
      </Modal>
    </React.Fragment>
  );
}
